import * as React from "react";

import { BACKEND_EVENTS } from "fieldpro-tools";
import { useDispatch } from "react-redux";

import useWebSocket from "../../hooks/useWebSocket";
import {
  toggleMigrationDialogActionCreator,
  toggleRefreshModalActionCreator,
  toggleUpdateDialogActionCreator,
} from "../../redux/actionCreators/appActionCreator";
import { clearCacheAndReload } from "../../utils/cacheBusting";
import { checkForUpdates } from "./checkForUpdates";
import {
  isMigrationOngoingEvent,
  isReleaseEvent,
  shouldShowNotification,
  updateNotificationRecord,
} from "./utils";
import vgLog from "./vgLog";

interface VersionGuardProps {
  children: React.ReactNode;
}

// VersionGuard: performs version checks and attaches common listeners
// Also monitors WebSocket events for backend-driven update/migration notifications
export const VersionGuard: React.FC<VersionGuardProps> = ({ children }) => {
  const dispatch = useDispatch();

  const dispatchToggleUpdateDialog = React.useCallback(() => {
    dispatch(toggleRefreshModalActionCreator(true, "update"));
  }, [dispatch]);

  const checkForUpdatesCallback = React.useCallback((cb?: () => void) => {
    checkForUpdates(cb);
  }, []);

  const runCheck = React.useCallback(
    (label?: string) => {
      if (label) vgLog("event:", label);
      checkForUpdatesCallback(dispatchToggleUpdateDialog);
    },
    [checkForUpdatesCallback, dispatchToggleUpdateDialog],
  );

  const handleVisibilityChange = React.useCallback(() => {
    vgLog("event: visibilitychange", document.visibilityState);

    if (document.visibilityState === "visible") {
      vgLog("visibility is visible, running check");
      runCheck();
    }
  }, [runCheck]);

  const handleFocus = React.useCallback(() => runCheck("focus"), [runCheck]);

  const handleOnline = React.useCallback(() => runCheck("online"), [runCheck]);

  // WebSocket handler for backend-driven update/migration events
  useWebSocket((msg) => {
    if (isReleaseEvent(msg)) {
      const type = BACKEND_EVENTS.NEW_RELEASE_FE;
      if (shouldShowNotification(type)) {
        dispatch(toggleUpdateDialogActionCreator(true));
        updateNotificationRecord(type);
      }
    }

    if (isMigrationOngoingEvent(msg)) {
      const type = BACKEND_EVENTS.IS_MIGRATION_ONGOING;
      if (shouldShowNotification(type)) {
        dispatch(toggleMigrationDialogActionCreator(true));
        updateNotificationRecord(type);
      }
    }
  });

  React.useEffect(() => {
    vgLog("effect: mount - starting initial check and registering listeners");

    checkForUpdatesCallback(() => {
      vgLog("initial check requested reload");
      clearCacheAndReload();
    });

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);

    return () => {
      vgLog("effect: unmount - removing listeners");
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
    };
  }, [
    checkForUpdatesCallback,
    handleVisibilityChange,
    handleFocus,
    handleOnline,
  ]);

  return <>{children}</>;
};
